import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, isRef as _isRef, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, vShow as _vShow, normalizeClass as _normalizeClass, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { id: "add-region-time" }
const _hoisted_2 = { id: "add-region-buttons" }
const _hoisted_3 = {
  key: 0,
  class: "my-selections",
  style: {"margin-top":"1em"}
}
const _hoisted_4 = {
  key: 0,
  class: "my-selections",
  style: {"margin-top":"1em"}
}
const _hoisted_5 = { class: "time-range-action-buttons" }
const _hoisted_6 = {
  key: 0,
  class: "my-selections",
  style: {"margin-top":"1em"}
}
const _hoisted_7 = {
  key: 0,
  class: "dataset-loading"
}
const _hoisted_8 = { class: "text-subtitle-2" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { class: "selection-icons" }
const _hoisted_11 = {
  key: 1,
  class: "pa-2 mb-2 error-explainer-text"
}
const _hoisted_12 = { class: "d-flex flex-wrap flex-row ma-2 align-center justify-center ga-1" }
const _hoisted_13 = {
  key: 4,
  class: "mixed-foldType-warning pa-2 ma-2 text-large text-red explainer-text"
}

import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { v4 } from "uuid";
import { supportsTouchscreen } from "@cosmicds/vue-toolkit";

import type { MillisecondRange, TimeRange, UserDataset, UnifiedRegion } from "../types";
import type { TimeRangeConfig } from "@/date_time_range_selection/date_time_range_generators";
import { serializeTempoStore, useTempoStore } from "../stores/app";
import { MOLECULE_OPTIONS } from "../esri/utils";
import { areEquivalentTimeRanges, formatTimeRange } from "../utils/timeRange";
import { atleast1d } from "../utils/atleast1d";

import DateTimeRangeSelection from "../date_time_range_selection/DateTimeRangeSelection.vue";
import AdvancedOperations from "./AdvancedOperations.vue";
import { TimeRangeSelectionType } from "@/types/datetime";
import PlotlyGraph from "./plotly/PlotlyGraph.vue";
import FoldedPlotlyGraph from "./FoldedPlotlyGraph.vue";
import CTextField from "./CTextField.vue";
import DatasetCard from "./DatasetCard.vue";
import { toZonedTime } from "date-fns-tz";
import { allEqual } from "@/utils/array_operations/array_math";
import { userDatasetToPlotly } from "@/utils/data_converters";
import UserDatasetTable from "./UserDatasetTable.vue";
import TimeRangeCard from "@/date_time_range_selection/TimeRangeCard.vue";

import UserDatasetEditor from "./UserDatasetEditor.vue";
type UnifiedRegionType = UnifiedRegion;


export default /*@__PURE__*/_defineComponent({
  __name: 'DatasetControls',
  setup(__props) {

const store = useTempoStore();
const {
  accentColor,
  accentColor2,
  backend,
  regions,
  datasets,
  timeRanges,
  singleDateSelected,
  uniqueDays,
  selectionActive,
  focusRegion,
  showSamplingPreviewMarkers,
  regionOpacity,
  regionVisibility,
  tempoRed,
} = storeToRefs(store);

const cssVars = computed(() => {
  return {
    '--accent-color': accentColor.value,
    '--accent-color-2': accentColor2.value,
  };
});

function plotlyDragPredicate(element: HTMLElement): boolean {
  return element.closest(".plotly") === null;
}

const touchscreen = supportsTouchscreen();

const openPanels = ref<number[]>([0, 1, 2]);
const openGraphs = ref<Record<string,boolean>>({});
const openSelection = ref<string | null>(null);
const tableSelection = ref<UserDataset | null>(null);
const currentlyEditingDataset = ref<UserDataset | null>(null);
const showConfirmReset = ref(false);

const createTimeRangeActive = ref(false);
const createDatasetActive = ref(false);
const datasetRowRefs = ref({});
const sampleErrorID = ref<string | null>(null);

const showEditRegionNameDialog = ref(false);
const regionBeingEdited = ref<UnifiedRegionType | null>(null);

const showEditTimeRangeNameDialog = ref(false);
const timeRangeBeingEdited = ref<TimeRange | null>(null);

const aggregationDataset = ref<UserDataset | null>(null);
const showAggregationDialog = ref(false);
function openAggregationDialog(selection: UserDataset) {
  aggregationDataset.value = selection;
  showAggregationDialog.value = true;
}
function handleAggregationSaved(aggregatedSelection: UserDataset) {
  aggregatedSelection.name = `${aggregatedSelection.name} ${datasets.value.length + 1}`;
  store.addDataset(aggregatedSelection, false); // no need to fetch anything
  showAggregationDialog.value = false;
  aggregationDataset.value = null;
}

function handleDatasetCreated(dataset: UserDataset) {
  dataset.name = `Dataset ${datasets.value.length + 1}`; // give it a default name
  store.addDataset(dataset);
  createDatasetActive.value = false;
}

const showDatasetEditor = ref(false);
const datasetEditorNameOnly = ref(false);
function handleEditDataset(dataset: UserDataset, nameOnly = false) {
  datasetEditorNameOnly.value = nameOnly;
  currentlyEditingDataset.value = dataset;
  showDatasetEditor.value = true;
}

function removeDataset(dataset: UserDataset) {
  store.deleteDataset(dataset);

  delete openGraphs[dataset.id];
  delete datasetRowRefs[dataset.id];
}

function handleDateTimeRangeSelectionChange(
  timeRanges: MillisecondRange[], 
  selectionType: TimeRangeSelectionType, 
  customName: string, 
  config: TimeRangeConfig,
) {
  if (!Array.isArray(timeRanges) || timeRanges.length === 0) {
    console.error('No time ranges received from DateTimeRangeSelection');
    return;
  }
  console.log(`Received ${timeRanges.length} time ranges of type ${selectionType} and name ${customName}`);
  const normalized = atleast1d(timeRanges);
  const countTimeRanges = store.timeRanges.length;
  // No dedup tracking now
  const tr: TimeRange = {
    id: v4(),
    name: `Time Range ${countTimeRanges + 1}`,
    description: customName,
    range: normalized.length === 1 ? normalized[0] : normalized,
    type: selectionType,
    config: config,
  };
  store.addTimeRange(tr);

  createTimeRangeActive.value = false;
  console.log(`Registered ${tr.name}: ${tr.description}`);
}

function editRegionName(region: UnifiedRegionType) {
  console.log(`Editing ${region.geometryType}: ${region.name}`);
  // Set the region to edit

  // eslint-disable-next-line
  // @ts-ignore it is not actually deep
  const existing = (regions.value as UnifiedRegionType[]).find(r => r.id === region.id);
  if (!existing) {
    console.error(`Region with ID ${region.id} not found.`);
    return;
  }
  regionBeingEdited.value = region;
  // Open dialog for renaming
  showEditRegionNameDialog.value = true;
}

function editTimeRangeName(timeRange: TimeRange) {
  console.log(`Editing time range: ${timeRange.name}`);
  
  const existing = timeRanges.value.find(tr => tr.id === timeRange.id);
  if (!existing) {
    console.error(`Time range with ID ${timeRange.id} not found.`);
    return;
  }
  timeRangeBeingEdited.value = timeRange;
  // Open dialog for renaming
  showEditTimeRangeNameDialog.value = true;
}

function graphTitle(dataset: UserDataset): string {
  const molecule = dataset.molecule;
  const molTitle = MOLECULE_OPTIONS.find(m => m.value === molecule)?.title || '';
  return `${molTitle} Time Series for ${dataset.region.name}`;
}

const showSelectedDatasetsGraph = ref(false);
const selectedDatasetsGraphData = computed(() =>{
  // check if folded
  if (selectedDatasets.value.length === 0) {
    return [];
  }
  const firstDataset = datasets.value.find(s => s.id === selectedDatasets.value[0]);
  if (!firstDataset) {
    return [];
  }
  const isFolded = firstDataset.timeRange?.type === 'folded';
  if (isFolded) {
    // folded datasets
    const validFoldedDatasets = datasets.value
      .filter(
        s => selectedDatasets.value.includes(s.id) &&
        s.timeRange?.type === 'folded' && 
        s.plotlyDatasets && 
        s.plotlyDatasets.length > 0
      );
    return validFoldedDatasets.map(s => {    
      return {
        ...s.plotlyDatasets![1],
        foldType: s.folded?.foldType,
        timezone: s.folded?.timezone,
        color: s.customColor || s.region.color,
      };
    }); // "!" tells TS that we know it's not undefined
  } else {
    // normal datasets
    return datasets.value
      .filter(s => selectedDatasets.value.includes(s.id))
      .map(s => userDatasetToPlotly(s, true));
  }
});

const selectedDatasetsGraphDataColors = computed<string[]>(() => {
  if (selectedDatasets.value.length === 0) {
    return [];
  }
  const firstDataset = datasets.value.find(s => s.id === selectedDatasets.value[0]);
  if (!firstDataset) {
    return [];
  }
  const isFolded = firstDataset.timeRange?.type === 'folded';
  if (isFolded) {
    // folded datasets
    const validFoldedDatasets = datasets.value
      .filter(
        s => selectedDatasets.value.includes(s.id) &&
        s.timeRange?.type === 'folded' && 
        s.plotlyDatasets && 
        s.plotlyDatasets.length > 0
      );
    return validFoldedDatasets.map(s => s.customColor || s.region.color);
  } else {
    // normal datasets
    return datasets.value
      .filter(s => selectedDatasets.value.includes(s.id))
      .map(s => s.customColor || s.region.color);
  }
  
});

const showNO2Graph = ref(false);
const no2GraphData = computed(() =>{
  return datasets.value.filter(s => s.molecule.includes('no2') && store.datasetHasSamples(s));
});

// ozone version
const showO3Graph = ref(false);
const o3GraphData = computed(() =>{
  return datasets.value.filter(s => s.molecule.includes('o3') && store.datasetHasSamples(s));
});


// formaldehyde version
const showHCHOGraph = ref(false);
const hchoGraphData = computed(() =>{
  return datasets.value.filter(s => s.molecule.includes('hcho') && store.datasetHasSamples(s));
});

function _toZonedTime(date: number | Date | string, timezone): number | Date {
  if (typeof date === 'number') {
    date = new Date(date);
  } else if (typeof date === 'string') {
    date = new Date(date);
  }
  return toZonedTime(date, timezone);
}

const allDatasetSelection = ref(false);
const selectedDatasets = ref<string[]>([]);
watch(allDatasetSelection, (newVal) => {
  if (!newVal) {
    selectedDatasets.value = [];
  }
});
watch(selectedDatasets, (newVal) => {
  console.log('Selected datasets changed:', newVal);
});

// is the current selection of datasets valid for graphing together?
const validDataSetSelection = computed<boolean>(() => {
  // must have same time range type and molecule
  const selected = datasets.value.filter(s => selectedDatasets.value.includes(s.id));
  if (selected.length === 0) {
    console.log('Not enough datasets selected for comparison');
    return true;
  }
  const firstMolecule = selected[0].molecule;
  const firstTimeRangeType = selected[0].timeRange;
  const notFolded = (tr: TimeRange) => tr?.type !== 'folded';
  const val = selected.every(s => s.molecule === firstMolecule && notFolded(s.timeRange) === notFolded(firstTimeRangeType));
  console.log('Valid dataset selection for graphing:', val, selected.map(s => ({id: s.id, molecule: s.molecule, timeRangeType: s.timeRange?.type})));
  return val;
});

const showfoldedNO2Graph = ref(false);
const no2foldedGraphData = computed(() =>{
  const validFoldedDatasets = datasets.value
    .filter(
      s => s.molecule.includes('no2') && 
      s.timeRange?.type === 'folded' && 
      s.plotlyDatasets && 
      s.plotlyDatasets.length > 0 &&
      selectedDatasets.value.includes(s.id)
    );
  return validFoldedDatasets.map(s => {    
    return {
      ...s.plotlyDatasets![1],
      foldType: s.folded?.foldType,
      timezone: s.folded?.timezone,
      color: s.customColor || s.region.color,
    };
  }); // "!" tells TS that we know it's not undefined
});

const showfoldedO3Graph = ref(false);
const o3foldedGraphData = computed(() =>{
  const validFoldedDatasets = datasets.value
    .filter(
      s => s.molecule.includes('o3') && 
      s.timeRange?.type === 'folded' && 
      s.plotlyDatasets && 
      s.plotlyDatasets.length > 0 &&
      selectedDatasets.value.includes(s.id)
    );
  return validFoldedDatasets.map(s => {
    return {
      ...s.plotlyDatasets![1],
      foldType: s.folded?.foldType,
      timezone: s.folded?.timezone,
      color: s.customColor || s.region.color,
    };
  }); // "!" tells TS that we know it's not undefined
}); 

const showfoldedHCHOGraph = ref(false);
const hchofoldedGraphData = computed(() =>{
  const validFoldedDatasets = datasets.value
    .filter(
      s => s.molecule.includes('hcho') && 
      s.timeRange?.type === 'folded' && 
      s.plotlyDatasets && 
      s.plotlyDatasets.length > 0 &&
      selectedDatasets.value.includes(s.id)
    );
  return validFoldedDatasets.map(s => {
    return {
      ...s.plotlyDatasets![1],
      foldType: s.folded?.foldType,
      timezone: s.folded?.timezone,
      color: s.customColor || s.region.color,
    };
  }); // "!" tells TS that we know it's not undefined
}); 

const showErrorBands = ref(true);

const showUserDatasetTable = ref(false);
watch(tableSelection, (newVal) => {
  if (newVal) {
    console.log('Table selection changed:', newVal);
    showUserDatasetTable.value = true;
  }
});


return (_ctx: any,_cache: any) => {
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_slider = _resolveComponent("v-slider")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_expansion_panel = _resolveComponent("v-expansion-panel")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_selection_composer = _resolveComponent("selection-composer")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_cds_dialog = _resolveComponent("cds-dialog")!
  const _component_v_toolbar_title = _resolveComponent("v-toolbar-title")!
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_v_expansion_panels = _resolveComponent("v-expansion-panels")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    id: "dataset-sections",
    style: _normalizeStyle(cssVars.value)
  }, [
    _createElementVNode("div", _hoisted_1, [
      _cache[58] || (_cache[58] = _createElementVNode("p", null, " Select a Region, Time Range, and Molecule to create a dataset of your choice! ", -1)),
      _createVNode(_component_v_dialog, {
        modelValue: showConfirmReset.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showConfirmReset).value = $event)),
        "max-width": "35%"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [...(_cache[48] || (_cache[48] = [
                  _createTextVNode(" Are you sure you want to delete all of your selections? This will remove all of your regions, time ranges, and datasets, and cannot be undone. ", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    color: "error",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (showConfirmReset.value = false))
                  }, {
                    default: _withCtx(() => [...(_cache[49] || (_cache[49] = [
                      _createTextVNode(" No ", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "success",
                    onClick: _cache[1] || (_cache[1] = () => {
              _unref(store).reset();
              _unref(serializeTempoStore)(_unref(store));
              showConfirmReset.value = false;
            })
                  }, {
                    default: _withCtx(() => [...(_cache[50] || (_cache[50] = [
                      _createTextVNode(" Yes ", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_expansion_panels, {
        modelValue: openPanels.value,
        "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((openPanels).value = $event)),
        variant: "accordion",
        id: "user-options-panels",
        multiple: "",
        class: "pl-3"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_expansion_panel, {
            title: "Regions",
            class: "mt-3 h3-panel-titles"
          }, {
            text: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_component_v_btn, {
                  size: "small",
                  active: _unref(selectionActive) === 'rectangle',
                  disabled: _unref(selectionActive) === 'point',
                  color: _unref(accentColor2),
                  onClick: _cache[3] || (_cache[3] = () => {
                  selectionActive.value = (_unref(selectionActive) === 'rectangle') ? null : 'rectangle';
                })
                }, {
                  prepend: _withCtx(() => [
                    (_unref(selectionActive) !== 'rectangle')
                      ? (_openBlock(), _createBlock(_component_v_icon, {
                          key: 0,
                          icon: "mdi-plus"
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  default: _withCtx(() => [
                    _createTextVNode(" " + _toDisplayString(_unref(selectionActive) === 'rectangle' ? "Cancel" : "New Region"), 1)
                  ]),
                  _: 1
                }, 8, ["active", "disabled", "color"])
              ]),
              _createVNode(_component_v_checkbox, {
                modelValue: _unref(showSamplingPreviewMarkers),
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(showSamplingPreviewMarkers) ? (showSamplingPreviewMarkers).value = $event : null)),
                label: "Show sample points",
                density: "compact",
                "hide-details": ""
              }, null, 8, ["modelValue"]),
              (_unref(regions).length>0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _cache[51] || (_cache[51] = _createElementVNode("h4", null, "My Regions", -1)),
                    _createVNode(_component_v_slider, {
                      modelValue: _unref(regionOpacity),
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(regionOpacity) ? (regionOpacity).value = $event : null)),
                      min: 0,
                      max: 1,
                      step: 0.01,
                      label: "Region Opacity",
                      color: _unref(tempoRed),
                      density: "compact",
                      "hide-details": ""
                    }, null, 8, ["modelValue", "color"]),
                    _createVNode(_component_v_checkbox, {
                      modelValue: _unref(regionVisibility),
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_isRef(regionVisibility) ? (regionVisibility).value = $event : null)),
                      label: "Show regions",
                      density: "compact",
                      "hide-details": ""
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_list, null, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(regions), (region, index) => {
                          return (_openBlock(), _createBlock(_component_v_list_item, {
                            key: index,
                            title: region.name,
                            style: _normalizeStyle({ 'background-color': region.color }),
                            onClick: () => focusRegion.value = region
                          }, {
                            append: _withCtx(() => [
                              _withDirectives(_createVNode(_component_v_btn, {
                                variant: "plain",
                                icon: "mdi-pencil",
                                color: "white",
                                onClick: (event) => {
                        editRegionName(region as UnifiedRegionType);
                        event.stopPropagation();
                      }
                              }, null, 8, ["onClick"]), [
                                [_directive_tooltip, 'Edit Name']
                              ]),
                              (!_unref(store).regionHasDatasets(region as UnifiedRegionType))
                                ? _withDirectives((_openBlock(), _createBlock(_component_v_btn, {
                                    key: 0,
                                    variant: "plain",
                                    icon: "mdi-delete",
                                    color: "white",
                                    onClick: (event) => {
                        _unref(store).deleteRegion(region as UnifiedRegionType);
                        event.stopPropagation();
                      }
                                  }, null, 8, ["onClick"])), [
                                    [_directive_tooltip, 'Delete']
                                  ])
                                : _createCommentVNode("", true)
                            ]),
                            _: 2
                          }, 1032, ["title", "style", "onClick"]))
                        }), 128))
                      ]),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createVNode(_component_v_expansion_panel, {
            title: "Time Ranges",
            class: "mt-3 h3-panel-titles"
          }, {
            text: _withCtx(() => [
              _createVNode(_component_v_btn, {
                size: "small",
                active: createTimeRangeActive.value,
                color: _unref(accentColor2),
                onClick: _cache[7] || (_cache[7] = ($event: any) => (createTimeRangeActive.value = !createTimeRangeActive.value))
              }, {
                prepend: _withCtx(() => [
                  (!createTimeRangeActive.value)
                    ? (_openBlock(), _createBlock(_component_v_icon, {
                        key: 0,
                        icon: "mdi-plus"
                      }))
                    : _createCommentVNode("", true)
                ]),
                default: _withCtx(() => [
                  _createTextVNode(" " + _toDisplayString(createTimeRangeActive.value ? "Cancel" : "New Time Range"), 1)
                ]),
                _: 1
              }, 8, ["active", "color"]),
              _withDirectives(_createVNode(DateTimeRangeSelection, {
                "current-date": _unref(singleDateSelected),
                "allowed-dates": _unref(uniqueDays),
                onRangesChange: handleDateTimeRangeSelectionChange,
                validate: ""
              }, null, 8, ["current-date", "allowed-dates"]), [
                [_vShow, createTimeRangeActive.value]
              ]),
              (_unref(timeRanges).length>0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _cache[52] || (_cache[52] = _createElementVNode("h4", null, "My Time Ranges", -1)),
                    _createVNode(_component_v_list, null, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(timeRanges), (timeRange, index) => {
                          return (_openBlock(), _createBlock(_component_v_list_item, {
                            class: "my-2 rounded-lg",
                            key: index,
                            style: {"background-color":"#444444"}
                          }, {
                            default: _withCtx(() => [
                              _createVNode(TimeRangeCard, {
                                name: timeRange.name === 'Displayed Day' ? `Displayed Day: ${ _unref(formatTimeRange)(timeRange.range) }` : (timeRange.name ?? _unref(formatTimeRange)(timeRange.range)),
                                "time-range": timeRange
                              }, null, 8, ["name", "time-range"]),
                              _createElementVNode("div", _hoisted_5, [
                                (timeRange.id !== 'displayed-day')
                                  ? _withDirectives((_openBlock(), _createBlock(_component_v_btn, {
                                      key: 0,
                                      variant: "plain",
                                      size: "x-small",
                                      icon: "mdi-pencil",
                                      color: "white",
                                      onClick: (event) => {
                        editTimeRangeName(timeRange);
                        event.stopPropagation();
                      }
                                    }, null, 8, ["onClick"])), [
                                      [_directive_tooltip, 'Edit Name']
                                    ])
                                  : _createCommentVNode("", true),
                                (timeRange.id !== 'displayed-day' && !_unref(datasets).some(s => _unref(areEquivalentTimeRanges)(s.timeRange, timeRange)))
                                  ? _withDirectives((_openBlock(), _createBlock(_component_v_btn, {
                                      key: 1,
                                      variant: "plain",
                                      size: "x-small",
                                      icon: "mdi-delete",
                                      color: "white",
                                      onClick: () => _unref(store).deleteTimeRange(timeRange)
                                    }, null, 8, ["onClick"])), [
                                      [_directive_tooltip, 'Delete']
                                    ])
                                  : _createCommentVNode("", true)
                              ])
                            ]),
                            _: 2
                          }, 1024))
                        }), 128))
                      ]),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createVNode(_component_v_divider, {
            class: "mt-3",
            opacity: "1"
          }),
          _createVNode(_component_v_expansion_panel, {
            title: "Datasets",
            class: "mt-3 h3-panel-titles"
          }, {
            text: _withCtx(() => [
              _createVNode(_component_v_btn, {
                size: "small",
                active: createDatasetActive.value,
                color: _unref(accentColor2),
                onClick: _cache[8] || (_cache[8] = ($event: any) => (createDatasetActive.value = !createDatasetActive.value)),
                class: "mt-3"
              }, {
                prepend: _withCtx(() => [
                  (!createDatasetActive.value)
                    ? (_openBlock(), _createBlock(_component_v_icon, {
                        key: 0,
                        icon: "mdi-plus"
                      }))
                    : _createCommentVNode("", true)
                ]),
                default: _withCtx(() => [
                  _createTextVNode(" " + _toDisplayString(createDatasetActive.value ? "Cancel" : "New Dataset"), 1)
                ]),
                _: 1
              }, 8, ["active", "color"]),
              _withDirectives(_createVNode(_component_selection_composer, {
                backend: _unref(backend),
                "time-ranges": _unref(timeRanges),
                regions: _unref(regions),
                disabled: { region: _unref(selectionActive) === 'rectangle', point: _unref(selectionActive) === 'point', timeRange: createTimeRangeActive.value },
                onCreate: handleDatasetCreated
              }, null, 8, ["backend", "time-ranges", "regions", "disabled"]), [
                [_vShow, createDatasetActive.value]
              ]),
              (_unref(datasets).length>0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _cache[57] || (_cache[57] = _createElementVNode("h4", null, "My Datasets", -1)),
                    _createVNode(DatasetCard, {
                      datasets: _unref(datasets),
                      "turn-on-selection": allDatasetSelection.value,
                      "selected-datasets": selectedDatasets.value,
                      "onUpdate:selectedDatasets": _cache[11] || (_cache[11] = ($event: any) => ((selectedDatasets).value = $event))
                    }, {
                      "action-row": _withCtx(({ isHovering, dataset }) => [
                        ((dataset.loading || !dataset.samples)  && !(dataset.timeRange?.type === 'folded' && dataset.plotlyDatasets))
                          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                              _cache[54] || (_cache[54] = _createElementVNode("hr", null, null, -1)),
                              _createVNode(_component_v_progress_linear, {
                                class: _normalizeClass(['dataset-loading-progress', !(dataset.loading && dataset.samples) ? 'dataset-loading-failed' : '']),
                                active: dataset.loading || !dataset.samples,
                                color: dataset.loading ? 'primary' : 'red',
                                indeterminate: dataset.loading,
                                value: !dataset.loading ? 100 : 0,
                                striped: !dataset.loading,
                                bottom: "",
                                rounded: "",
                                height: "20"
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("span", _hoisted_8, _toDisplayString(dataset.loading ? 'Data Loading' : (!dataset.samples ? 'Error Loading Data' : '')), 1)
                                ]),
                                _: 2
                              }, 1032, ["class", "active", "color", "indeterminate", "value", "striped"]),
                              _createVNode(_component_v_tooltip, {
                                text: "Remove selection",
                                location: "top"
                              }, {
                                activator: _withCtx(({ props }) => [
                                  _createVNode(_component_v_btn, _mergeProps(props, {
                                    size: "x-small",
                                    icon: "mdi-trash-can",
                                    variant: "plain",
                                    onClick: () => removeDataset(dataset)
                                  }), null, 16, ["onClick"])
                                ]),
                                _: 2
                              }, 1024),
                              (!(dataset.loading || dataset.samples || dataset.plotlyDatasets))
                                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                    _cache[53] || (_cache[53] = _createElementVNode("hr", null, null, -1)),
                                    _createVNode(_component_v_tooltip, {
                                      text: "Failure info",
                                      location: "top"
                                    }, {
                                      activator: _withCtx(({ props }) => [
                                        _createVNode(_component_v_btn, _mergeProps(props, {
                                          size: "x-small",
                                          icon: "mdi-help-circle",
                                          variant: "plain",
                                          onClick: () => sampleErrorID.value = dataset.id
                                        }), null, 16, ["onClick"])
                                      ]),
                                      _: 2
                                    }, 1024),
                                    _createVNode(_component_v_tooltip, {
                                      text: "Remove selection",
                                      location: "top"
                                    }, {
                                      activator: _withCtx(({ props }) => [
                                        _createVNode(_component_v_btn, _mergeProps(props, {
                                          size: "x-small",
                                          icon: "mdi-trash-can",
                                          variant: "plain",
                                          onClick: () => removeDataset(dataset)
                                        }), null, 16, ["onClick"])
                                      ]),
                                      _: 2
                                    }, 1024)
                                  ]))
                                : _createCommentVNode("", true)
                            ]))
                          : _createCommentVNode("", true),
                        _createVNode(_component_v_expand_transition, null, {
                          default: _withCtx(() => [
                            _withDirectives(_createElementVNode("div", _hoisted_10, [
                              (dataset.timeRange.type === 'single' || dataset.folded)
                                ? (_openBlock(), _createBlock(_component_v_tooltip, {
                                    key: 0,
                                    text: "Show graph",
                                    location: "top"
                                  }, {
                                    activator: _withCtx(({ props }) => [
                                      _createVNode(_component_v_btn, _mergeProps(props, {
                                        size: "x-small",
                                        icon: "mdi-chart-line",
                                        disabled: !(dataset.samples || dataset.plotlyDatasets),
                                        variant: "plain",
                                        onClick: () => openGraphs.value[dataset.id] = true
                                      }), null, 16, ["disabled", "onClick"])
                                    ]),
                                    _: 2
                                  }, 1024))
                                : (_openBlock(), _createBlock(_component_v_tooltip, {
                                    key: 1,
                                    text: "Graph Data",
                                    location: "top"
                                  }, {
                                    activator: _withCtx(({ props }) => [
                                      _createVNode(_component_v_btn, _mergeProps(props, {
                                        size: "x-small",
                                        icon: "mdi-chart-line",
                                        disabled: !dataset.samples,
                                        variant: "plain",
                                        onClick: () => openAggregationDialog(dataset)
                                      }), null, 16, ["disabled", "onClick"])
                                    ]),
                                    _: 2
                                  }, 1024)),
                              _createVNode(_component_v_tooltip, {
                                text: "Show table",
                                location: "top"
                              }, {
                                activator: _withCtx(({ props }) => [
                                  _createVNode(_component_v_btn, _mergeProps(props, {
                                    size: "x-small",
                                    icon: "mdi-table",
                                    disabled: !dataset.samples && !dataset.folded,
                                    variant: "plain",
                                    onClick: () => tableSelection.value = dataset
                                  }), null, 16, ["disabled", "onClick"])
                                ]),
                                _: 2
                              }, 1024),
                              _createVNode(_component_v_tooltip, {
                                text: "Edit Dataset Name/Color",
                                location: "top"
                              }, {
                                activator: _withCtx(({ props }) => [
                                  _createVNode(_component_v_btn, _mergeProps(props, {
                                    size: "x-small",
                                    icon: "mdi-pencil",
                                    variant: "plain",
                                    onClick: () => handleEditDataset(dataset)
                                  }), null, 16, ["onClick"])
                                ]),
                                _: 2
                              }, 1024),
                              _createVNode(_component_v_spacer),
                              _createVNode(_component_v_tooltip, {
                                text: "Remove selection",
                                location: "top"
                              }, {
                                activator: _withCtx(({ props }) => [
                                  _createVNode(_component_v_btn, _mergeProps(props, {
                                    size: "x-small",
                                    icon: "mdi-trash-can",
                                    variant: "plain",
                                    onClick: () => removeDataset(dataset)
                                  }), null, 16, ["onClick"])
                                ]),
                                _: 2
                              }, 1024)
                            ], 512), [
                              [_vShow, (dataset.samples || dataset.plotlyDatasets) && (_unref(touchscreen) ? openSelection.value == dataset.id : isHovering || true)]
                            ])
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_component_cds_dialog, {
                          title: graphTitle(dataset),
                          modelValue: openGraphs.value[dataset.id],
                          "onUpdate:modelValue": ($event: any) => ((openGraphs.value[dataset.id]) = $event),
                          "title-color": "var(--info-background)",
                          draggable: "",
                          persistent: "",
                          scrim: false,
                          modal: false,
                          "drag-predicate": plotlyDragPredicate
                        }, {
                          default: _withCtx(() => [
                            (dataset.timeRange.type === 'folded' && dataset.plotlyDatasets)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createVNode(_component_v_checkbox, {
                                    modelValue: showErrorBands.value,
                                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((showErrorBands).value = $event)),
                                    label: "Show Errors",
                                    density: "compact",
                                    "hide-details": ""
                                  }, null, 8, ["modelValue"]),
                                  _createVNode(FoldedPlotlyGraph, {
                                    datasets: dataset.plotlyDatasets,
                                    "show-errors": showErrorBands.value,
                                    colors: [dataset.customColor ?? dataset.region.color, '#333'],
                                    "data-options": [{mode: 'markers'}, {mode: 'markers'}],
                                    names: [`Original Data`, `Binned`],
                                    "layout-options": {width: 600, height: 400},
                                    "fold-type": dataset.folded?.foldType,
                                    timezones: dataset.folded?.timezone
                                  }, null, 8, ["datasets", "show-errors", "colors", "fold-type", "timezones"])
                                ], 64))
                              : (_openBlock(), _createBlock(PlotlyGraph, {
                                  key: 1,
                                  datasets: dataset ? [_unref(userDatasetToPlotly)(dataset, true)] : [],
                                  colors: [dataset.customColor || dataset.region.color],
                                  "show-errors": "",
                                  "data-options": [{mode: 'markers'}],
                                  names: dataset.name ? [dataset.name] : undefined,
                                  "layout-options": {
                            width: 600, 
                            height: 400,
                            // https://plotly.com/javascript/reference/layout/xaxis/#layout-xaxis-title-text
                            xaxis: {title: {text: 'Local Time for Region'}},
                            }
                                }, null, 8, ["datasets", "colors", "names"])),
                            _cache[55] || (_cache[55] = _createElementVNode("div", {
                              class: "explainer-text",
                              style: {"width":"500px","margin-inline":"auto"}
                            }, " Note: We do not allow aggregation of data for a single day. Please create or select a longer dataset if you would like to explore more ", -1))
                          ]),
                          _: 2
                        }, 1032, ["title", "modelValue", "onUpdate:modelValue"]),
                        _createVNode(_component_v_dialog, {
                          "model-value": sampleErrorID.value !== null,
                          "max-width": "50%"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card, null, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_toolbar, { density: "compact" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_toolbar_title, { text: "Error Loading Data" }),
                                    _createVNode(_component_v_spacer),
                                    _createVNode(_component_v_btn, {
                                      icon: "mdi-close",
                                      onClick: _cache[10] || (_cache[10] = ($event: any) => (sampleErrorID.value = null))
                                    })
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_v_card_text, null, {
                                  default: _withCtx(() => [...(_cache[56] || (_cache[56] = [
                                    _createTextVNode(" There was an error loading data for this selection. Either there is no data for the region/time range/molecule combination that you selected, or there was an error loading data from the server. You can delete this selection and try making a new one. ", -1)
                                  ]))]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["model-value"])
                      ]),
                      _: 1
                    }, 8, ["datasets", "turn-on-selection", "selected-datasets"])
                  ]))
                : _createCommentVNode("", true),
              (!validDataSetSelection.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, " Note: You can only overlay datasets with the same molecule and fold type. "))
                : _createCommentVNode("", true),
              (_unref(datasets).length > 1)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 2,
                    disabled: _unref(datasets).length === 0 || !_unref(datasets).every(d => d.samples || d.plotlyDatasets),
                    color: "#ffcc33",
                    size: "small",
                    block: false,
                    onClick: _cache[12] || (_cache[12] = ($event: any) => (allDatasetSelection.value = !allDatasetSelection.value))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(allDatasetSelection.value ? 'Cancel Selection' : 'Select Datasets to Graph'), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _createElementVNode("div", _hoisted_12, [
      (selectedDatasets.value.length > 0)
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 0,
            color: _unref(accentColor2),
            disabled: !validDataSetSelection.value,
            onClick: _cache[14] || (_cache[14] = ($event: any) => (showSelectedDatasetsGraph.value = true))
          }, {
            default: _withCtx(() => [...(_cache[59] || (_cache[59] = [
              _createTextVNode(" Graph Selected Datasets ", -1)
            ]))]),
            _: 1
          }, 8, ["color", "disabled"]))
        : _createCommentVNode("", true),
      _createVNode(_component_cds_dialog, {
        title: "Graph of Selected Datasets",
        modelValue: showSelectedDatasetsGraph.value,
        "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((showSelectedDatasetsGraph).value = $event)),
        draggable: "",
        persistent: "",
        modal: false,
        scrim: false,
        "drag-predicate": plotlyDragPredicate
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_checkbox, {
            modelValue: showErrorBands.value,
            "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((showErrorBands).value = $event)),
            label: "Show Errors",
            density: "compact",
            "hide-details": ""
          }, null, 8, ["modelValue"]),
          _createVNode(PlotlyGraph, {
            datasets: selectedDatasetsGraphData.value,
            colors: selectedDatasetsGraphDataColors.value,
            "show-errors": showErrorBands.value,
            "data-options": selectedDatasetsGraphData.value.map(() => ({mode: 'markers'})),
            names: selectedDatasetsGraphData.value.map(d => d.name ?? ''),
            "layout-options": {
          width: 600, 
          height: 400,
          xaxis: {title: {text: 'Local Time for Region'}},
        }
          }, null, 8, ["datasets", "colors", "show-errors", "data-options", "names"])
        ]),
        _: 1
      }, 8, ["modelValue"]),
      (false && no2GraphData.value.length > 0)
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 1,
            onClick: _cache[17] || (_cache[17] = ($event: any) => (showNO2Graph.value = true))
          }, {
            default: _withCtx(() => [...(_cache[60] || (_cache[60] = [
              _createTextVNode(" Show NO₂ Graph ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_cds_dialog, {
        title: "Nitrogen Dioxide Data",
        modelValue: showNO2Graph.value,
        "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((showNO2Graph).value = $event)),
        draggable: "",
        persistent: "",
        modal: false,
        scrim: false,
        "drag-predicate": plotlyDragPredicate
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_checkbox, {
            modelValue: showErrorBands.value,
            "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((showErrorBands).value = $event)),
            label: "Show Errors",
            density: "compact",
            "hide-details": ""
          }, null, 8, ["modelValue"]),
          _createVNode(PlotlyGraph, {
            datasets: no2GraphData.value.map(d => _unref(userDatasetToPlotly)(d, true)),
            colors: no2GraphData.value.map(d => d.customColor || d.region.color),
            "show-errors": showErrorBands.value,
            "data-options": no2GraphData.value.map(() => ({mode: 'markers'})),
            names: no2GraphData.value.map(d => d.name ?? ''),
            "layout-options": {
          width: 600, 
          height: 400,
          xaxis: {title: {text: 'Local Time for Region'}},
        }
          }, null, 8, ["datasets", "colors", "show-errors", "data-options", "names"])
        ]),
        _: 1
      }, 8, ["modelValue"]),
      (false && o3GraphData.value.length > 0)
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 2,
            onClick: _cache[20] || (_cache[20] = ($event: any) => (showO3Graph.value = true))
          }, {
            default: _withCtx(() => [...(_cache[61] || (_cache[61] = [
              _createTextVNode(" Show Ozone Graph ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_cds_dialog, {
        title: "Ozone Data",
        modelValue: showO3Graph.value,
        "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((showO3Graph).value = $event)),
        draggable: "",
        persistent: "",
        modal: false,
        scrim: false,
        "drag-predicate": plotlyDragPredicate
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_checkbox, {
            modelValue: showErrorBands.value,
            "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((showErrorBands).value = $event)),
            label: "Show Errors",
            density: "compact",
            "hide-details": ""
          }, null, 8, ["modelValue"]),
          _createVNode(PlotlyGraph, {
            datasets: o3GraphData.value.map(d => _unref(userDatasetToPlotly)(d, true)),
            colors: o3GraphData.value.map(d => d.customColor || d.region.color),
            "show-errors": showErrorBands.value,
            "data-options": o3GraphData.value.map(() => ({mode: 'markers'})),
            names: o3GraphData.value.map(d => d.name ?? ''),
            "layout-options": {
          width: 600, 
          height: 400,
          xaxis: {title: {text: 'Local Time for Region'}},
        }
          }, null, 8, ["datasets", "colors", "show-errors", "data-options", "names"])
        ]),
        _: 1
      }, 8, ["modelValue"]),
      (false && hchoGraphData.value.length > 0)
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 3,
            onClick: _cache[23] || (_cache[23] = ($event: any) => (showHCHOGraph.value = true))
          }, {
            default: _withCtx(() => [...(_cache[62] || (_cache[62] = [
              _createTextVNode(" Show Formaldehyde Graph ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_cds_dialog, {
        title: "Formaldehyde Data",
        modelValue: showHCHOGraph.value,
        "onUpdate:modelValue": _cache[25] || (_cache[25] = ($event: any) => ((showHCHOGraph).value = $event)),
        draggable: "",
        persistent: "",
        modal: false,
        scrim: false,
        "drag-predicate": plotlyDragPredicate
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_checkbox, {
            modelValue: showErrorBands.value,
            "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event: any) => ((showErrorBands).value = $event)),
            label: "Show Errors",
            density: "compact",
            "hide-details": ""
          }, null, 8, ["modelValue"]),
          _createVNode(PlotlyGraph, {
            datasets: hchoGraphData.value.map(d => _unref(userDatasetToPlotly)(d, true)),
            colors: hchoGraphData.value.map(d => d.customColor || d.region.color),
            "show-errors": showErrorBands.value,
            "data-options": hchoGraphData.value.map(() => ({mode: 'markers'})),
            names: hchoGraphData.value.map(d => d.name ?? ''),
            "layout-options": {
          width: 600, 
          height: 400,
          xaxis: {title: {text: 'Local Time for Region'}},
        }
          }, null, 8, ["datasets", "colors", "show-errors", "data-options", "names"])
        ]),
        _: 1
      }, 8, ["modelValue"]),
      (!_unref(allEqual)(no2foldedGraphData.value.map(v => v.foldType)) || 
            !_unref(allEqual)(o3foldedGraphData.value.map(v => v.foldType)) || 
            !_unref(allEqual)(hchofoldedGraphData.value.map(v => v.foldType)))
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, " Warning: Be sure to only select datasets that have the same type of fold. It is not possible to overlay different FoldTypes. "))
        : _createCommentVNode("", true),
      (false && no2foldedGraphData.value.length > 0)
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 5,
            onClick: _cache[26] || (_cache[26] = ($event: any) => (showfoldedNO2Graph.value = true))
          }, {
            default: _withCtx(() => [...(_cache[63] || (_cache[63] = [
              _createTextVNode(" Show Folded NO₂ Data ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_cds_dialog, {
        title: "Nitrogen Dioxide Data",
        modelValue: showfoldedNO2Graph.value,
        "onUpdate:modelValue": _cache[28] || (_cache[28] = ($event: any) => ((showfoldedNO2Graph).value = $event)),
        draggable: "",
        persistent: "",
        modal: false,
        scrim: false,
        "drag-predicate": plotlyDragPredicate
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_checkbox, {
            modelValue: showErrorBands.value,
            "onUpdate:modelValue": _cache[27] || (_cache[27] = ($event: any) => ((showErrorBands).value = $event)),
            label: "Show Errors",
            density: "compact",
            "hide-details": ""
          }, null, 8, ["modelValue"]),
          _createVNode(FoldedPlotlyGraph, {
            datasets: no2foldedGraphData.value.length > 0 ? no2foldedGraphData.value : [],
            "show-errors": showErrorBands.value,
            colors: no2foldedGraphData.value.map( v => v.color) ?? ['#FF5733', '#333'],
            "data-options": [{mode: 'markers'}, {mode: 'markers'}],
            "layout-options": {width: 600, height: 400},
            "fold-type": no2foldedGraphData.value[0].foldType,
            timezones: no2foldedGraphData.value.map(v => v.timezone) ?? 'UTC'
          }, null, 8, ["datasets", "show-errors", "colors", "fold-type", "timezones"])
        ]),
        _: 1
      }, 8, ["modelValue"]),
      (false && o3foldedGraphData.value.length > 0)
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 6,
            onClick: _cache[29] || (_cache[29] = ($event: any) => (showfoldedO3Graph.value = true))
          }, {
            default: _withCtx(() => [...(_cache[64] || (_cache[64] = [
              _createTextVNode(" Show Folded O₃ Data ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_cds_dialog, {
        title: "Ozone Data",
        modelValue: showfoldedO3Graph.value,
        "onUpdate:modelValue": _cache[31] || (_cache[31] = ($event: any) => ((showfoldedO3Graph).value = $event)),
        draggable: "",
        persistent: "",
        modal: false,
        scrim: false,
        "drag-predicate": plotlyDragPredicate
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_checkbox, {
            modelValue: showErrorBands.value,
            "onUpdate:modelValue": _cache[30] || (_cache[30] = ($event: any) => ((showErrorBands).value = $event)),
            label: "Show Errors",
            density: "compact",
            "hide-details": ""
          }, null, 8, ["modelValue"]),
          _createVNode(FoldedPlotlyGraph, {
            datasets: o3foldedGraphData.value.length > 0 ? o3foldedGraphData.value : [],
            "show-errors": showErrorBands.value,
            colors: o3foldedGraphData.value.map( v => v.color) ?? ['#FF5733', '#333'],
            "data-options": [{mode: 'markers'}, {mode: 'markers'}],
            "layout-options": {width: 600, height: 400},
            "fold-type": o3foldedGraphData.value[0].foldType,
            timezones: o3foldedGraphData.value.map(v => v.timezone) ?? 'UTC'
          }, null, 8, ["datasets", "show-errors", "colors", "fold-type", "timezones"])
        ]),
        _: 1
      }, 8, ["modelValue"]),
      (false && hchofoldedGraphData.value.length > 0)
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 7,
            onClick: _cache[32] || (_cache[32] = ($event: any) => (showfoldedHCHOGraph.value = true))
          }, {
            default: _withCtx(() => [...(_cache[65] || (_cache[65] = [
              _createTextVNode(" Show Folded HCHO Data ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_cds_dialog, {
        title: "Formaldehyde Data",
        modelValue: showfoldedHCHOGraph.value,
        "onUpdate:modelValue": _cache[34] || (_cache[34] = ($event: any) => ((showfoldedHCHOGraph).value = $event)),
        draggable: "",
        persistent: "",
        modal: false,
        scrim: false,
        "drag-predicate": plotlyDragPredicate
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_checkbox, {
            modelValue: showErrorBands.value,
            "onUpdate:modelValue": _cache[33] || (_cache[33] = ($event: any) => ((showErrorBands).value = $event)),
            label: "Show Errors",
            density: "compact",
            "hide-details": ""
          }, null, 8, ["modelValue"]),
          _createVNode(FoldedPlotlyGraph, {
            datasets: hchofoldedGraphData.value.length > 0 ? hchofoldedGraphData.value : [],
            "show-errors": showErrorBands.value,
            colors: hchofoldedGraphData.value.map( v => v.color) ?? ['#FF5733', '#333'],
            "data-options": [{mode: 'markers'}, {mode: 'markers'}],
            "layout-options": {width: 600, height: 400},
            "fold-type": hchofoldedGraphData.value[0].foldType,
            timezones: hchofoldedGraphData.value.map(v => v.timezone) ?? 'UTC'
          }, null, 8, ["datasets", "show-errors", "colors", "fold-type", "timezones"])
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _cache[67] || (_cache[67] = _createElementVNode("hr", {
        class: "ma-2",
        style: {"width":"100%"}
      }, null, -1)),
      (_unref(regions).length > 0 || _unref(timeRanges).length > 1)
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 8,
            color: "#a63a3f",
            onClick: _cache[35] || (_cache[35] = ($event: any) => (showConfirmReset.value = true))
          }, {
            default: _withCtx(() => [...(_cache[66] || (_cache[66] = [
              _createTextVNode(" Delete ALL selections ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_v_dialog, {
      modelValue: showEditRegionNameDialog.value,
      "onUpdate:modelValue": _cache[38] || (_cache[38] = ($event: any) => ((showEditRegionNameDialog).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(CTextField, {
          label: "Region Name",
          title: "Enter a new name for this region",
          "hide-details": "",
          dense: "",
          "button-color": _unref(accentColor),
          onConfirm: _cache[36] || (_cache[36] = (name) => {
            if (regionBeingEdited.value) {
              _unref(store).setRegionName(regionBeingEdited.value as UnifiedRegionType, name);
              showEditRegionNameDialog.value = false;
            }
          }),
          onCancel: _cache[37] || (_cache[37] = () => {
            showEditRegionNameDialog.value = false;
            regionBeingEdited.value = null;
          })
        }, null, 8, ["button-color"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: showEditTimeRangeNameDialog.value,
      "onUpdate:modelValue": _cache[41] || (_cache[41] = ($event: any) => ((showEditTimeRangeNameDialog).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(CTextField, {
          label: "Time Range Name",
          title: "Enter a new name for this time range",
          "hide-details": "",
          dense: "",
          "button-color": _unref(accentColor),
          onConfirm: _cache[39] || (_cache[39] = (name) => {
            if (timeRangeBeingEdited.value) {
              _unref(store).setTimeRangeName(timeRangeBeingEdited.value, name);
              showEditTimeRangeNameDialog.value = false;
            }
          }),
          onCancel: _cache[40] || (_cache[40] = () => {
            showEditTimeRangeNameDialog.value = false;
            timeRangeBeingEdited.value = null;
          })
        }, null, 8, ["button-color"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: showDatasetEditor.value,
      "onUpdate:modelValue": _cache[44] || (_cache[44] = ($event: any) => ((showDatasetEditor).value = $event))
    }, {
      default: _withCtx(() => [
        (currentlyEditingDataset.value !== null)
          ? (_openBlock(), _createBlock(UserDatasetEditor, {
              key: 0,
              modelValue: currentlyEditingDataset.value,
              "onUpdate:modelValue": _cache[42] || (_cache[42] = ($event: any) => ((currentlyEditingDataset).value = $event)),
              "name-only": datasetEditorNameOnly.value,
              onComplete: _cache[43] || (_cache[43] = () => {
            showDatasetEditor.value = false;
            currentlyEditingDataset.value = null;
          })
            }, null, 8, ["modelValue", "name-only"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(AdvancedOperations, {
      modelValue: showAggregationDialog.value,
      "onUpdate:modelValue": _cache[45] || (_cache[45] = ($event: any) => ((showAggregationDialog).value = $event)),
      selection: aggregationDataset.value,
      onSave: handleAggregationSaved
    }, null, 8, ["modelValue", "selection"]),
    _createVNode(_component_v_dialog, {
      modelValue: showUserDatasetTable.value,
      "onUpdate:modelValue": _cache[47] || (_cache[47] = ($event: any) => ((showUserDatasetTable).value = $event)),
      "max-width": "800px",
      persistent: ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_toolbar, { density: "compact" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_toolbar_title, { text: "Dataset Table View" }),
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  icon: "mdi-close",
                  onClick: _cache[46] || (_cache[46] = ($event: any) => {showUserDatasetTable.value = false; tableSelection.value = null})
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                (tableSelection.value !== null)
                  ? (_openBlock(), _createBlock(UserDatasetTable, {
                      key: 0,
                      dataset: tableSelection.value
                    }, null, 8, ["dataset"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 4))
}
}

})